import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Container,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  CheckCircle as AcceptedIcon,
  Cancel as RejectedIcon,
  HourglassEmpty as PendingIcon,
  Visibility as EyeIcon,
  DirectionsWalk as FootIcon,
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import Layout from '../components/Layout';
import { dashboardAPI } from '../services/api';

const HospitalDashboard = () => {
  const { user } = useAuth();
  const [hospitalData, setHospitalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHospitalData();
  }, [selectedYear]);

  const loadHospitalData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = selectedYear ? { year: selectedYear } : {};
      const response = await dashboardAPI.getHospitalReferralStats(params);
      setHospitalData(response.data?.hospitals || response.data || []);
    } catch (error) {
      console.error('Error loading hospital data:', error);
      setError('Failed to load hospital referral data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
          <CircularProgress size={64} />
        </Box>
      </Layout>
    );
  }

  const StatCard = ({ title, value, icon, color, bgColor }) => (
    <Card elevation={0} sx={{ border: `2px solid ${color}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color, fontSize: '2rem' }}>
          {value || 0}
        </Typography>
      </CardContent>
    </Card>
  );

  // Aggregate totals across all hospitals
  const totals = hospitalData.reduce((acc, h) => ({
    eyeReferrals: acc.eyeReferrals + parseInt(h.eye_referrals || 0),
    footReferrals: acc.footReferrals + parseInt(h.foot_referrals || 0),
    accepted: acc.accepted + parseInt(h.accepted || 0),
    rejected: acc.rejected + parseInt(h.rejected || 0),
    pending: acc.pending + parseInt(h.pending || 0),
    total: acc.total + parseInt(h.total_referrals || 0),
  }), { eyeReferrals: 0, footReferrals: 0, accepted: 0, rejected: 0, pending: 0, total: 0 });

  // Chart data
  const chartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      stacked: true,
      toolbar: { show: true },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 8,
        borderRadiusApplication: 'end',
      },
    },
    colors: ['#10b981', '#ef4444', '#f59e0b'],
    xaxis: {
      categories: hospitalData.map(h => h.hospital_Name),
      labels: {
        rotate: -45,
        style: { fontSize: '11px', fontWeight: 600 },
      },
    },
    yaxis: {
      title: { text: 'Number of Referrals' },
    },
    legend: {
      position: 'bottom',
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
    },
  };

  const chartSeries = [
    { name: 'Accepted', data: hospitalData.map(h => parseInt(h.accepted || 0)) },
    { name: 'Rejected', data: hospitalData.map(h => parseInt(h.rejected || 0)) },
    { name: 'Pending', data: hospitalData.map(h => parseInt(h.pending || 0)) },
  ];

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                Hospital Referral Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track referral acceptance and processing by hospital
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <MenuItem value="">All Years</MenuItem>
                <MenuItem value="2026">2026</MenuItem>
                <MenuItem value="2025">2025</MenuItem>
                <MenuItem value="2024">2024</MenuItem>
                <MenuItem value="2023">2023</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Total Referrals"
                value={totals.total}
                icon={<HospitalIcon />}
                color="#667eea"
                bgColor="#e0e7ff"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Eye Referrals"
                value={totals.eyeReferrals}
                icon={<EyeIcon />}
                color="#3b82f6"
                bgColor="#dbeafe"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Foot Referrals"
                value={totals.footReferrals}
                icon={<FootIcon />}
                color="#10b981"
                bgColor="#d1fae5"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Accepted"
                value={totals.accepted}
                icon={<AcceptedIcon />}
                color="#10b981"
                bgColor="#d1fae5"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Rejected"
                value={totals.rejected}
                icon={<RejectedIcon />}
                color="#ef4444"
                bgColor="#fee2e2"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <StatCard
                title="Pending"
                value={totals.pending}
                icon={<PendingIcon />}
                color="#f59e0b"
                bgColor="#fef3c7"
              />
            </Grid>
          </Grid>

          {/* Stacked Bar Chart */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Referral Status by Hospital
                </Typography>
                <ReactApexChart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={400}
                />
              </Paper>
            </Grid>
          </Grid>

          {/* Individual Hospital Cards */}
          <Grid container spacing={3}>
            {hospitalData.map((hospital) => {
              const totalRefs = parseInt(hospital.total_referrals || 0);
              const acceptanceRate = totalRefs > 0 
                ? ((parseInt(hospital.accepted || 0) / totalRefs) * 100).toFixed(1) 
                : 0;

              return (
                <Grid item xs={12} md={6} key={hospital.hospital_id}>
                  <Paper elevation={0} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: 2, height: '100%' }}>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <HospitalIcon sx={{ fontSize: 32, color: '#667eea' }} />
                      <Box flex={1}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                          {hospital.hospital_Name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hospital.hospital_region}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${acceptanceRate}% Acceptance`}
                        color={acceptanceRate >= 80 ? 'success' : acceptanceRate >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#dbeafe', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Eye Referrals
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e40af' }}>
                            {hospital.eye_referrals || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ p: 1.5, backgroundColor: '#d1fae5', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Foot Referrals
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#065f46' }}>
                            {hospital.foot_referrals || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary" display="block">
                            Accepted
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                            {hospital.accepted || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary" display="block">
                            Rejected
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                            {hospital.rejected || 0}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box textAlign="center">
                          <Typography variant="caption" color="text.secondary" display="block">
                            Pending
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                            {hospital.pending || 0}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Layout>
  );
};

export default HospitalDashboard;